import React from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Trend } from '../../../types/dashboard';

interface EnhancedDashboardCardProps {
  title: string;
  value: number;
  previousValue?: number;
  growthRate?: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  trend?: Trend | 'up' | 'down' | 'neutral';
  trendValue?: number;
  trendLabel?: string;
  subtitle?: string;
  description?: string;
  unit?: string;
  className?: string;
  onClick?: () => void;
}

const EnhancedDashboardCard: React.FC<EnhancedDashboardCardProps> = ({
  title,
  value,
  previousValue,
  growthRate,
  icon,
  color,
  bgColor,
  trend = 'neutral',
  trendValue,
  trendLabel,
  subtitle,
  description,
  unit = '',
  className = '',
  onClick
}) => {
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-GB').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatPercentage = (num: number) => {
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const getTrendIcon = () => {
    const trendDirection = typeof trend === 'object' ? trend.direction : trend;
    switch (trendDirection) {
      case 'up':
        return <TrendingUp className="w-4 h-4" />;
      case 'down':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };

  const getTrendColor = () => {
    const trendDirection = typeof trend === 'object' ? trend.direction : trend;
    switch (trendDirection) {
      case 'up':
        return 'text-green-600 dark:text-green-400';
      case 'down':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-slate-500 dark:text-slate-400';
    }
  };

  const getTrendBgColor = () => {
    const trendDirection = typeof trend === 'object' ? trend.direction : trend;
    switch (trendDirection) {
      case 'up':
        return 'bg-green-100 dark:bg-green-900/20';
      case 'down':
        return 'bg-red-100 dark:bg-red-900/20';
      default:
        return 'bg-slate-100 dark:bg-slate-800';
    }
  };

  const getArrowIcon = () => {
    const trendDirection = typeof trend === 'object' ? trend.direction : trend;
    switch (trendDirection) {
      case 'up':
        return <ArrowUpRight className="w-3 h-3" />;
      case 'down':
        return <ArrowDownRight className="w-3 h-3" />;
      default:
        return null;
    }
  };

  return (
    <div 
      className={`m3-surface rounded-2xl p-6 m3-elevation-2 hover:m3-elevation-4 transition-all duration-500 group relative overflow-hidden hover-lift cursor-pointer ${className}`}
      onClick={onClick}
    >
      {/* Background solid overlay */}
      <div className={`absolute inset-0 ${bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center m3-elevation-1 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
              <span className="text-white text-xl">{icon}</span>
            </div>
            <div>
              <h3 className="m3-label-large text-slate-600 dark:text-slate-300 font-medium">{title}</h3>
              {subtitle && (
                <p className="m3-label-small text-slate-500 dark:text-slate-400">{subtitle}</p>
              )}
            </div>
          </div>
          
          {/* Trend indicator */}
          {trend !== 'neutral' && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getTrendBgColor()}`}>
              {getTrendIcon()}
              <span className={`text-xs font-medium ${getTrendColor()}`}>
                {trendValue !== undefined ? formatPercentage(trendValue) : formatPercentage(growthRate || 0)}
              </span>
            </div>
          )}
        </div>

        {/* Main value */}
        <div className="mb-3">
          <p className="m3-headline-large text-slate-800 dark:text-white group-hover:scale-105 transition-transform duration-300 font-bold">
            {title.toLowerCase().includes('revenue') || title.toLowerCase().includes('income') || title.toLowerCase().includes('profit') 
              ? formatCurrency(value) 
              : formatNumber(value)
            }{unit}
          </p>
        </div>

        {/* Growth details */}
        {(growthRate !== undefined || previousValue !== undefined) && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-lg ${getTrendBgColor()}`}>
                {getArrowIcon()}
                <span className={`text-xs font-medium ${getTrendColor()}`}>
                  {growthRate !== undefined 
                    ? formatPercentage(growthRate)
                    : previousValue !== undefined 
                      ? formatPercentage(((value - previousValue) / previousValue) * 100)
                      : '0.0%'
                  }
                </span>
              </div>
              {trendLabel && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {trendLabel}
                </span>
              )}
            </div>
            
            {previousValue !== undefined && (
              <div className="text-right">
                <p className="text-xs text-slate-500 dark:text-slate-400">Previous</p>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {title.toLowerCase().includes('revenue') || title.toLowerCase().includes('income') || title.toLowerCase().includes('profit') 
                    ? formatCurrency(previousValue) 
                    : formatNumber(previousValue)
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* Live indicator */}
        <div className="flex items-center justify-center space-x-2 mt-3">
          <div className={`w-2 h-2 ${color.includes('blue') ? 'bg-primary' : color.includes('green') ? 'bg-green-500' : color.includes('orange') ? 'bg-orange-500' : 'bg-slate-500'} rounded-full animate-pulse`}></div>
          <span className="m3-label-small text-slate-500 dark:text-slate-400 font-medium">Live</span>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboardCard;
